// Trigger layer: right-click (any pointer type) and a 600ms/30px-tolerance long-press for finger
// touch only, both delegated on `root` so redrawn cells/events need no re-binding. Same numbers as
// the jQuery version it replaces (js/calendar.v2.8.js:596-671), ported to Pointer Events (D2).
//
// Pen is deliberately excluded from long-press (2026-09-23 product decision): pen is for precise
// tapping/selection ("input"), finger is for gestures ("movement"). This also sidesteps whatever
// pen-reliability concern originally motivated D2 -- pen no longer attempts this gesture at all, so
// its behavior on that path is moot. Pencil users still get the normal tap-to-open click, and
// right-click still works for any pointer type via the `contextmenu` listener below.
//
// KNOWN GAP for finger, still open pending real-device data (see docs/plans/t2-bridge-and-context-
// menu.md's D2 follow-up): Chromium fires a native pointercancel once touch movement passes its own
// internal pan-gesture threshold (~10-15px), pre-empting this file's 30px tolerance check entirely
// -- the old touchstart-based version never had this because raw Touch Events aren't pre-empted by
// pan-gesture arbitration the same way. Real-world tremor tolerance today is whatever Chromium's
// platform slop is, not 30px. Fixing this for real means switching finger's gesture-tracking to raw
// Touch Events (no CSS trade-off) -- deliberately deferred until real-device testing shows whether
// finger tremor is an actual problem, not just a theoretical one.

import { dispatch } from './dispatch';

const LONG_PRESS_DELAY_MS = 600;
const MOVE_TOLERANCE_PX = 30;
const CLICK_SUPPRESS_MS = 400;

export function installContextMenuTriggers(root: HTMLElement): void {
  root.addEventListener('contextmenu', (e: MouseEvent) => {
    // Only suppress the native menu when ours opened, so inputs and links elsewhere under `root` keep theirs
    if (e.target instanceof Element && dispatch(e.clientX, e.clientY, e.target)) e.preventDefault();
  });

  let timer: number | null = null;
  let startX = 0;
  let startY = 0;
  let activePointerId: number | null = null;

  function cancel(): void {
    if (timer !== null) {
      window.clearTimeout(timer);
      timer = null;
    }
    activePointerId = null;
  }

  root.addEventListener('pointerdown', (e: PointerEvent) => {
    if (e.pointerType !== 'touch') return; // pen excluded from long-press, see the module comment
    if (!(e.target instanceof Element)) return;
    cancel();
    activePointerId = e.pointerId;
    startX = e.clientX;
    startY = e.clientY;
    const { clientX, clientY, target } = e;
    timer = window.setTimeout(() => {
      timer = null;
      dispatch(clientX, clientY, target);
      const suppressNextClick = (evt: Event): void => {
        evt.preventDefault();
        evt.stopImmediatePropagation();
      };
      document.addEventListener('click', suppressNextClick, true);
      window.setTimeout(() => document.removeEventListener('click', suppressNextClick, true), CLICK_SUPPRESS_MS);
    }, LONG_PRESS_DELAY_MS);
  });

  root.addEventListener('pointermove', (e: PointerEvent) => {
    if (e.pointerId !== activePointerId) return;
    if (Math.abs(e.clientX - startX) > MOVE_TOLERANCE_PX || Math.abs(e.clientY - startY) > MOVE_TOLERANCE_PX) cancel();
  });

  root.addEventListener('pointerup', (e: PointerEvent) => {
    if (e.pointerId === activePointerId) cancel();
  });
  root.addEventListener('pointercancel', (e: PointerEvent) => {
    if (e.pointerId === activePointerId) cancel();
  });
}
